// src/data/roadmaps.ts

import { RoadmapWeek, RoadmapMetadata, CriticalGap, CategoryScore, RiskLevel } from '../types';

export function generateRoadmap(
  criticalGaps: CriticalGap[],
  categoryScores: CategoryScore[],
  riskLevel: RiskLevel,
  organizationSize: 'small' | 'medium' | 'large'
): RoadmapWeek[] {
  const template = getRoadmapTemplate(organizationSize);
  const phases = generatePhases(organizationSize);
  return mapPhasesToWeeks(phases, template);
}

function getRoadmapTemplate(orgSize: 'small' | 'medium' | 'large') {
  const templates = {
    small: { weeks: 12, phases: 3, phaseWeeks: [3, 4, 5] },
    medium: { weeks: 14, phases: 3, phaseWeeks: [4, 5, 5] },
    large: { weeks: 20, phases: 4, phaseWeeks: [4, 5, 6, 5] },
  };
  return templates[orgSize];
}

function generatePhases(orgSize: 'small' | 'medium' | 'large') {
  return [
    { name: 'Phase 1: Critical Foundations', priority: 'CRITICAL' as const, duration: orgSize === 'small' ? 3 : 4 },
    { name: 'Phase 2: Technical Controls', priority: 'CRITICAL' as const, duration: orgSize === 'small' ? 4 : 5 },
    { name: 'Phase 3: Operational Controls', priority: 'HIGH' as const, duration: orgSize === 'small' ? 5 : orgSize === 'medium' ? 5 : 6 },
    ...(orgSize === 'large' ? [{ name: 'Phase 4: Optimization', priority: 'MEDIUM' as const, duration: 5 }] : []),
  ];
}

function mapPhasesToWeeks(phases: any[], template: any): RoadmapWeek[] {
  const weeks: RoadmapWeek[] = [];
  let currentWeek = 1;

  phases.forEach((phase, phaseIdx) => {
    for (let i = 0; i < phase.duration; i++) {
      weeks.push({
        week: currentWeek++,
        title: `${phase.name} - Week ${i + 1}`,
        phase: `Phase ${phaseIdx + 1}` as any,
        priority: phase.priority,
        tasks: getPhaseTasks(phaseIdx, i, phase.duration),
      });
    }
  });

  return weeks;
}

function getPhaseTasks(phaseIdx: number, weekIdx: number, totalWeeks: number) {
  const allTasks = [
    // Phase 1
    [[
      { task: 'Designate Security Officer', action: 'Appoint qualified individual', resources: ['Job description'], estimatedTime: '5-10 hours', owner: 'Executive' },
      { task: 'Schedule Risk Assessment', action: 'Hire assessor or prepare team', resources: ['Assessment template'], estimatedTime: '10-15 hours', owner: 'Security Officer' },
    ], [
      { task: 'Conduct Risk Assessment', action: 'Identify systems and threats', resources: ['Risk checklist'], estimatedTime: '20-30 hours', owner: 'Security Officer' },
    ], [
      { task: 'Develop Security Policies', action: 'Create core policies', resources: ['Policy templates'], estimatedTime: '20-40 hours', owner: 'Compliance Officer' },
    ]],
    // Phase 2
    [[
      { task: 'Implement Encryption - At Rest', action: 'Enable disk/database encryption', resources: ['Encryption tools'], estimatedTime: '30-50 hours', owner: 'IT Team' },
    ], [
      { task: 'Implement Encryption - In Transit', action: 'TLS/SSL for all connections', resources: ['SSL certificates'], estimatedTime: '20-30 hours', owner: 'Network Team' },
    ], [
      { task: 'Configure Access Controls', action: 'Implement RBAC', resources: ['Identity management'], estimatedTime: '40-60 hours', owner: 'IT Team' },
    ], [
      { task: 'Enable Audit Logging', action: 'Configure PHI access logging', resources: ['SIEM tools'], estimatedTime: '20-40 hours', owner: 'Security Team' },
    ]],
    // Phase 3
    [[
      { task: 'Security Awareness Training', action: 'Deploy annual training', resources: ['Training platform'], estimatedTime: '20-30 hours', owner: 'Compliance/HR' },
    ], [
      { task: 'Incident Response Plan', action: 'Document breach procedures', resources: ['IR template'], estimatedTime: '15-25 hours', owner: 'Compliance Officer' },
    ], [
      { task: 'Execute BAAs', action: 'Sign BAAs with all vendors', resources: ['BAA template'], estimatedTime: '10-20 hours', owner: 'Legal' },
    ], [
      { task: 'Disaster Recovery Plan', action: 'Test backup/recovery', resources: ['Backup system'], estimatedTime: '20-30 hours', owner: 'IT Team' },
    ]],
    // Phase 4
    [[
      { task: 'Security Monitoring', action: 'Set up continuous monitoring', resources: ['SIEM platform'], estimatedTime: '30-50 hours', owner: 'SOC' },
    ], [
      { task: 'Compliance Automation', action: 'Automate reporting', resources: ['GRC tools'], estimatedTime: '40-60 hours', owner: 'Compliance/IT' },
    ]],
  ];

  if (phaseIdx >= allTasks.length) return [];
  const phaseTasks = allTasks[phaseIdx];
  const tasksPerWeek = Math.ceil(phaseTasks.length / totalWeeks);
  const startIdx = weekIdx * tasksPerWeek;
  return phaseTasks.slice(startIdx, startIdx + tasksPerWeek).flat();
}

export function generateRoadmapMetadata(organizationSize: 'small' | 'medium' | 'large'): RoadmapMetadata {
  const metadata = {
    small: {
      organizationSize: 'small' as const,
      totalWeeks: 12,
      totalPhases: 3,
      estimatedCost: '$50,000-$150,000',
      assumptions: ['Part-time compliance role', 'Limited IT staff', 'Simple infrastructure'],
      riskFactors: ['Staff turnover', 'Resource constraints', 'Limited expertise'],
    },
    medium: {
      organizationSize: 'medium' as const,
      totalWeeks: 14,
      totalPhases: 3,
      estimatedCost: '$150,000-$400,000',
      assumptions: ['Dedicated compliance officer', 'IT department exists', 'Multiple departments'],
      riskFactors: ['Cross-department coordination', 'Competing priorities', 'Legacy systems'],
    },
    large: {
      organizationSize: 'large' as const,
      totalWeeks: 20,
      totalPhases: 4,
      estimatedCost: '$400,000-$1,000,000+',
      assumptions: ['Compliance team', 'Multiple IT teams', 'Complex infrastructure', 'Multiple locations'],
      riskFactors: ['Stakeholder management', 'Integration risks', 'Multi-location coordination'],
    },
  };
  return metadata[organizationSize];
}
